import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SuggestionRow, FoundBox } from './SuggestionRow';
import { homesStrip, stripTicks, sortedHomes, rangeCaps, spanShort } from '../../lib/orgProfile';
import { RESPITE_PATTERNS, respitePatternLabel, formatMonthYear } from '../../lib/orgTypes';
import { describeSuggestion } from '../../lib/suggestions';

export const HOME_LABELS = {
  foster_placement: 'Foster home',
  kinship: 'Family (kinship care)',
  supported_lodgings: 'Supported lodgings',
  childrens_home: "Children's home",
  residential: 'Residential unit',
  semi_independent: 'Semi-independent',
};
const SUGGESTED_KIND = {
  foster_placement: 'Foster carers', kinship: 'Family carer', supported_lodgings: 'Supported lodgings',
  childrens_home: "Children's home", residential: 'Residential unit', semi_independent: 'Semi-independent',
};

export const homeName = (p) => p?.name || (p?.carer_names?.length ? p.carer_names.join(' and ') : 'A home');

/**
 * The strip of homes across the years: one block per home sized by how long
 * you were there, gaps kept blank. Decorative — the cards underneath say the
 * same in words — so it's hidden from screen readers.
 */
export const HomesStrip = ({ placements, label }) => {
  const strip = homesStrip(placements);
  if (!strip || strip.segments.filter((s) => !s.gap).length < 1) return null;
  const ticks = stripTicks(strip.startYear, strip.endYear);
  return (
    <div className="space-y-1.5" aria-hidden="true" data-testid="homes-strip">
      {label && <p className="pd-eyebrow">{label}</p>}
      <div className="flex gap-[3px] h-11">
        {strip.segments.map((s, i) => (
          <div
            key={s.gap ? `gap-${i}` : s.placement.id || i}
            style={{ flexGrow: s.months, flexBasis: 0 }}
            className={`min-w-[6px] flex items-center px-2.5 overflow-hidden whitespace-nowrap text-[13px] font-semibold ${s.gap ? 'bg-ground' : 'bg-inset text-ink'} ${i === 0 ? 'rounded-l-md' : ''} ${i === strip.segments.length - 1 ? 'rounded-r-md' : ''}`}
          >
            {!s.gap && <span className="truncate">{homeName(s.placement)}</span>}
          </div>
        ))}
      </div>
      <div className="flex justify-between pd-mono">{ticks.map((y) => <span key={y}>{y}</span>)}</div>
    </div>
  );
};

const ShortBreaks = ({ org, editing }) => {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ placementId: '', name: '', carerId: null, pattern: '' });
  const homes = sortedHomes(org.placements || []).filter((p) => p.id);
  const rows = (() => {
    const grouped = {};
    for (const p of homes) {
      for (const e of p.respite || []) {
        const carer = editing.respitePool.find((c) => c.id === e.carer_id);
        if (!carer) continue;
        grouped[carer.id] = grouped[carer.id] || { ...carer, stays: [] };
        grouped[carer.id].stays.push({ placement: p, pattern: e.pattern });
      }
    }
    return Object.values(grouped);
  })();
  if (!homes.length) return null;
  const matches = draft.name.trim()
    ? editing.respitePool.filter((c) => c.name?.toLowerCase().includes(draft.name.trim().toLowerCase()) && c.id !== draft.carerId).slice(0, 5)
    : [];
  const save = async () => {
    const ok = await editing.attachRespite(draft.placementId || homes[0].id, { carerId: draft.carerId, name: draft.name.trim(), pattern: draft.pattern });
    if (ok) { setAdding(false); setDraft({ placementId: '', name: '', carerId: null, pattern: '' }); }
  };

  return (
    <div className="space-y-3" data-testid="short-breaks-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">Short breaks</h3>
        {!adding && <button type="button" onClick={() => setAdding(true)} className="text-sm font-semibold text-brand hover:text-brand-ink" data-testid="add-short-break">Add a short break</button>}
      </div>
      {rows.length === 0 && !adding && (
        <p className="text-[15px] text-ink-55">Anyone who looked after you for weekends, school holidays or while your carers were away. None added.</p>
      )}
      {rows.map((r) => (
        <div key={r.id} className="rounded-lg bg-ground p-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3" data-testid={`short-break-${r.id}`}>
          <div className="min-w-0 space-y-1">
            <p className="font-semibold text-ink">{r.name}{r.linked_org_id && <Link to={`/organisations/${r.linked_org_id}`} className="ml-2 text-sm font-semibold text-brand hover:text-brand-ink">Its own page →</Link>}</p>
            <ul className="text-sm text-ink-70 space-y-0.5">
              {r.stays.map((st) => (
                <li key={st.placement.id} className="flex flex-wrap items-center gap-x-2">
                  <span><span className="text-ink-55">While at </span>{homeName(st.placement)}{st.pattern ? <span className="text-ink-55"> · {respitePatternLabel(st.pattern).toLowerCase()}</span> : null}</span>
                  <button type="button" onClick={() => editing.detachRespite(st.placement.id, r.id)} disabled={editing.saving} className="text-sm text-ink-55 hover:text-breach-ink" aria-label={`Remove ${r.name} from ${homeName(st.placement)}`}>Remove</button>
                </li>
              ))}
            </ul>
          </div>
          {!r.linked_org_id && (
            <Button size="sm" variant="ghost" disabled={editing.saving} onClick={() => editing.promoteRespite(r.id)} className="shrink-0" data-testid={`promote-respite-${r.id}`}>This was a children&apos;s home</Button>
          )}
        </div>
      ))}
      {adding && (
        <div className="rounded-lg bg-ground p-4 space-y-3" data-testid="short-break-form">
          <Label className="text-[15px] font-semibold" htmlFor="sb-name">Who looked after you for short breaks?</Label>
          <div className="grid sm:grid-cols-3 gap-2">
            <Input id="sb-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value, carerId: null })} placeholder="A name, or a family name" className="bg-surface border-surface" data-testid="short-break-name" />
            <Select value={draft.placementId || homes[0].id} onValueChange={(v) => setDraft({ ...draft, placementId: v })}>
              <SelectTrigger className="bg-surface border-surface" aria-label="While you lived at"><SelectValue /></SelectTrigger>
              <SelectContent>{homes.map((p) => <SelectItem key={p.id} value={p.id}>While at {homeName(p)}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={draft.pattern} onValueChange={(v) => setDraft({ ...draft, pattern: v })}>
              <SelectTrigger className="bg-surface border-surface" aria-label="How often" data-testid="short-break-pattern"><SelectValue placeholder="How often?" /></SelectTrigger>
              <SelectContent>{RESPITE_PATTERNS.map((pt) => <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {matches.length > 0 && (
            <div className="flex flex-wrap gap-1.5 items-center">
              <span className="text-sm text-ink-55">Already known:</span>
              {matches.map((c) => (
                <button key={c.id} type="button" onClick={() => setDraft({ ...draft, name: c.name, carerId: c.id })} className="px-2.5 py-1 rounded-full border border-dashed border-brand text-brand text-sm hover:bg-brand-wash">{c.name}</button>
              ))}
            </div>
          )}
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" size="sm" onClick={() => setAdding(false)}>Cancel</Button>
            <Button size="sm" disabled={editing.saving || !draft.name.trim()} onClick={save} data-testid="short-break-save">Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * "Where you lived" — the homes this council or agency placed you in, read
 * first (strip, then a card per home with how much was written while you
 * were there), suggestions from the records inside the section, short
 * breaks underneath.
 */
export const OrgHomes = ({ org, profile, editing, onAddHome }) => {
  const homes = sortedHomes(org.placements || []);
  const pending = org.pending_suggestions || [];
  const s = (type) => pending.filter((x) => x.type === type);
  const placementSuggestions = s('placement');
  const detailSuggestions = s('placement_details');
  const homeOrgSuggestions = pending.filter((x) => x.type === 'home_organisation' || x.type === 'link_home');
  const respiteSuggestions = s('respite');
  const found = placementSuggestions.length + detailSuggestions.length + homeOrgSuggestions.length + respiteSuggestions.length;
  const orgsById = Object.fromEntries(editing.allOrgs.map((o) => [o.id, o]));
  const confirm = (id, opts) => editing.confirmSuggestions([id], opts);
  const ignore = (id) => editing.rejectSuggestions([id]);

  return (
    <section aria-labelledby="org-homes-heading" className="space-y-4" data-testid="placements-card">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="org-homes-heading" className="font-heading text-[24px] font-semibold tracking-[-0.02em] text-ink">Where you lived</h2>
        <button type="button" onClick={onAddHome} className="text-sm font-semibold text-brand hover:text-brand-ink" data-testid="add-placement-btn">Add a home</button>
      </div>

      {homes.length === 0 ? (
        <div className="rounded-lg bg-ground py-8 px-6 text-center">
          <p className="font-heading text-[22px] font-semibold tracking-[-0.02em] text-ink mb-1">No homes added yet</p>
          <p className="text-base text-ink-55 mb-4 max-w-md mx-auto">Add the places you lived while {org.name} was responsible for you. Your request names them, so they know where to look.</p>
          <Button size="sm" variant="secondary" onClick={onAddHome} data-testid="add-placement-empty-btn">Add a home</Button>
        </div>
      ) : (
        <>
          <HomesStrip placements={homes} />
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {homes.map((p, i) => {
              const records = profile?.placements?.[p.id]?.records;
              const breaks = (p.respite || []).map((e) => editing.respitePool.find((c) => c.id === e.carer_id)?.name).filter(Boolean);
              const range = rangeCaps(p.start_date, p.end_date, 'STILL THERE');
              const span = p.start_date ? spanShort(p.start_date, p.end_date) : null;
              const to = p.linked_org_id ? `/organisations/${p.linked_org_id}` : p.id ? `/organisations/${org.id}/placements/${p.id}` : null;
              return (
                <div key={p.id || i} className="rounded-lg bg-ground p-5 flex flex-col gap-1.5" data-testid={`placement-item-${i}`}>
                  {range && <p className="pd-mono">{range}{span ? ` · ${span}` : ''}</p>}
                  <h3 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">
                    {to ? <Link to={to} className="hover:text-brand" data-testid={p.linked_org_id ? `placement-open-org-${i}` : `placement-view-${i}`}>{homeName(p)}</Link> : homeName(p)}
                  </h3>
                  <p className="text-sm text-ink-55">
                    {[HOME_LABELS[p.type] || 'Home', p.linked_org_id ? 'has its own page' : null, p.address].filter(Boolean).join(' · ')}
                  </p>
                  {p.name && (p.carer_names || []).length > 0 && <p className="text-sm text-ink-70">With {p.carer_names.join(' and ')}</p>}
                  {(records || breaks.length > 0) && (
                    <p className="text-sm text-ink-70">
                      {[records ? `${records} record${records === 1 ? '' : 's'} from this time` : null, breaks.length ? `short breaks with ${breaks.join(', ')}` : null].filter(Boolean).join(' · ')}
                    </p>
                  )}
                  {!p.linked_org_id && p.id && (
                    <div className="pt-1">
                      <button type="button" onClick={() => editing.removePlacement(p.id)} disabled={editing.saving} className="text-sm text-ink-55 hover:text-breach-ink" aria-label={`Remove ${homeName(p)}`} data-testid={`delete-placement-${i}`}>Remove</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {found > 0 && (
        <FoundBox testId="home-suggestions">
          {placementSuggestions.map((sg) => (
            <SuggestionRow
              key={sg.id} testId={`placement-suggestion-${sg.id}`} disabled={editing.saving}
              title={sg.carer_name || sg.home_name || 'A home'}
              detail={[
                SUGGESTED_KIND[sg.placement_type] || 'Foster carers',
                sg.start_date ? `${formatMonthYear(sg.start_date)}${sg.end_date ? ` – ${formatMonthYear(sg.end_date)}` : ''}` : null,
                sg.address,
                sg.target_org_id && sg.target_org_id !== org.id ? `would go under ${orgsById[sg.target_org_id]?.name || 'another organisation'}` : null,
                sg.can_be_organisation ? 'if a company or charity ran it, it holds its own records too' : null,
              ].filter(Boolean).join(' · ')}
              actions={[
                { label: 'Add this home', onClick: () => confirm(sg.id), testId: `confirm-suggestion-${sg.id}` },
                ...(sg.can_be_organisation ? [{ label: 'And as an organisation', onClick: () => confirm(sg.id, { alsoOrganisation: true }), testId: `confirm-suggestion-with-org-${sg.id}` }] : []),
              ]}
              onIgnore={() => ignore(sg.id)}
            />
          ))}
          {[...homeOrgSuggestions, ...detailSuggestions].map((sg) => {
            const { title, detail } = describeSuggestion(sg, org, orgsById);
            return (
              <SuggestionRow
                key={sg.id} testId={`home-suggestion-${sg.id}`} disabled={editing.saving} title={title} detail={detail}
                actions={[{ label: sg.type === 'home_organisation' ? 'Add organisation' : sg.type === 'link_home' ? 'Add this home' : 'Add', onClick: () => confirm(sg.id), testId: `confirm-suggestion-${sg.id}` }]}
                onIgnore={() => ignore(sg.id)}
              />
            );
          })}
          {respiteSuggestions.map((sg) => (
            <SuggestionRow
              key={sg.id} testId={`respite-suggestion-${sg.id}`} disabled={editing.saving}
              title={sg.carer_name}
              detail={`Short breaks${sg.placement_id ? ` · while you lived at ${homeName(homes.find((h) => h.id === sg.placement_id))}` : ''}`}
              actions={[{ label: 'Add', onClick: () => confirm(sg.id), testId: `confirm-suggestion-${sg.id}` }]}
              onIgnore={() => ignore(sg.id)}
            />
          ))}
        </FoundBox>
      )}

      <ShortBreaks org={org} editing={editing} />
    </section>
  );
};

export default OrgHomes;
